// PlaceDetailModal.jsx - 修复版本

import React, { useState } from 'react';
import { Modal, Button, Row, Col, Badge, Carousel } from 'react-bootstrap';
import './PlaceDetailModal.css';

const PlaceDetailModal = ({ show, onHide, placeData, placeName }) => {
    if (!placeData) return null;

    // 模拟的图片数据（实际项目中从 placeData 获取）
    const images = placeData.images || [
        placeData.image_url,
        // 可以添加更多图片
    ].filter(Boolean);

    // 模拟的营业时间数据
    const getHours = () => {
        const hours = placeData.hours || {
            'Mon': 'Closed',
            'Tue': '6-10 PM',
            'Wed': '6-10 PM', 
            'Thu': '6-10 PM',
            'Fri': '6-10:30 PM',
            'Sat': '5:30-10:30 PM',
            'Sun': 'Closed'
        };
        return hours;
    };

    const getCurrentStatus = () => {
        const now = new Date();
        const currentDay = now.toLocaleDateString('en-US', { weekday: 'short' });
        const currentHour = now.getHours();
        
        // 简单的营业状态判断
        if (currentHour >= 17 && currentHour <= 22) {
            return { status: 'Open now', color: 'success' };
        }
        return { status: 'Closed', color: 'danger' };
    };

    const status = getCurrentStatus();

    return (
        <Modal 
            show={show} 
            onHide={onHide} 
            size="lg" 
            centered
            className="place-detail-modal"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <div className="w-100">
                    <Modal.Title className="place-modal-title">
                        {placeName}
                    </Modal.Title>
                    <div className="place-modal-subtitle">
                        <span className="rating">
                            ⭐ {placeData.rating || '4.6'} 
                        </span>
                        <span className="reviews">
                            · {placeData.review_count || '2.1k'} reviews
                        </span>
                        <span className="location">
                            · Montreal, Canada
                        </span>
                    </div>
                    <div className="place-tags">
                        {placeData.cuisine && (
                            <Badge bg="light" text="dark" className="me-2">
                                🍽️ {placeData.cuisine}
                            </Badge>
                        )}
                        {placeData.price_level && (
                            <Badge bg="light" text="dark" className="me-2">
                                {'$'.repeat(placeData.price_level)}
                            </Badge>
                        )}
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="p-0">
                {/* 图片轮播 */}
                {images.length > 0 && (
                    <div className="place-images-section">
                        {images.length === 1 ? (
                            <img 
                                src={images[0]} 
                                alt={placeName}
                                className="single-place-image"
                            />
                        ) : (
                            <Carousel>
                                {images.map((img, index) => (
                                    <Carousel.Item key={index}>
                                        <img
                                            src={img}
                                            alt={`${placeName} ${index + 1}`}
                                            className="carousel-place-image"
                                        />
                                    </Carousel.Item>
                                ))}
                            </Carousel>
                        )}
                    </div>
                )}

                <div className="place-content p-4">
                    {/* 概述部分 */}
                    <div className="place-section mb-4">
                        <h6 className="section-title">Overview</h6>
                        <p className="place-description">
                            {placeData.activity_desc || placeData.description || 
                            `${placeName} is a distinguished restaurant offering exceptional dining experience in Montreal. Known for its exquisite cuisine and elegant atmosphere.`}
                        </p>
                    </div>

                    {/* 信息网格 */}
                    <Row className="place-info-grid">
                        {/* 地址 */}
                        <Col md={6} className="mb-3">
                            <div className="info-item">
                                <div className="info-icon">📍</div>
                                <div className="info-content">
                                    <div className="info-label">Address</div>
                                    <div className="info-value">
                                        {placeData.address || '423 Rue Saint-Claude'}
                                        <br />
                                        Montreal, QC H2Y 3B6
                                        <br />
                                        Canada
                                    </div>
                                </div>
                            </div>
                        </Col>

                        {/* 网站 */}
                        {placeData.website && (
                            <Col md={6} className="mb-3">
                                <div className="info-item">
                                    <div className="info-icon">🌐</div>
                                    <div className="info-content">
                                        <div className="info-label">Website</div>
                                        <a 
                                            href={placeData.website} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="info-link"
                                        >
                                            Visit website
                                        </a>
                                    </div>
                                </div>
                            </Col>
                        )}

                        {/* 电话 */}
                        <Col md={6} className="mb-3">
                            <div className="info-item">
                                <div className="info-icon">📞</div>
                                <div className="info-content">
                                    <div className="info-label">Phone</div>
                                    <div className="info-value">
                                        {placeData.phone || '+1 514 861 1112'}
                                    </div>
                                </div>
                            </div>
                        </Col>

                        {/* 营业时间 */}
                        <Col md={6} className="mb-3">
                            <div className="info-item">
                                <div className="info-icon">🕒</div>
                                <div className="info-content">
                                    <div className="info-label">Hours of operation</div>
                                    <div className="info-value">
                                        <Badge 
                                            bg={status.color} 
                                            className="status-badge mb-2"
                                        >
                                            {status.status}
                                        </Badge>
                                        <div className="hours-list">
                                            {Object.entries(getHours()).map(([day, hours]) => (
                                                <div key={day} className="hours-item">
                                                    <span className="day">{day}</span>
                                                    <span className="hours">{hours}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* 行程信息（如果有） */}
                    {(placeData.start_time || placeData.avg_cost_per_person) && (
                        <div className="place-section mt-4">
                            <h6 className="section-title">Your Itinerary</h6>
                            <Row>
                                {placeData.start_time && (
                                    <Col md={6}>
                                        <div className="itinerary-item">
                                            <strong>Scheduled Time:</strong>
                                            <br />
                                            {placeData.start_time}
                                            {placeData.end_time && ` - ${placeData.end_time}`}
                                        </div>
                                    </Col>
                                )}
                                {placeData.avg_cost_per_person && (
                                    <Col md={6}>
                                        <div className="itinerary-item">
                                            <strong>Estimated Cost:</strong>
                                            <br />
                                            ${placeData.avg_cost_per_person} per person
                                        </div>
                                    </Col>
                                )}
                            </Row>
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer className="border-0 pt-0">
                <div className="w-100 d-flex justify-content-between align-items-center">
                    <div className="modal-actions-left">
                        <Button variant="outline-primary" className="me-2">
                            ❤️ Save
                        </Button>
                        <Button variant="outline-primary">
                            📤 Share
                        </Button>
                    </div>
                    <div className="modal-actions-right">
                        <Button variant="primary">
                            🗺️ View on Map
                        </Button>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default PlaceDetailModal;